'use client';

import { handleDispatchInteractionEvent } from '@/helpers/adobe';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import './news.scss';

type NewsProps = {
    news: {
        Titulo: string;
        Categoria: string;
        Autor: string;
        Url: string;
        UrlPrincipalImage: string;
        AutorAvatar: string;
    }[];
};

const News = ({ news }: NewsProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });

    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!emblaApi) return;

        const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
        emblaApi.on('select', onSelect);
        onSelect();

        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi]);

    const safeNews = Array.isArray(news);

    return (
        <div className="news" data-testid="news-component" id="news-component">
            <h2 data-testid="news-title">
                Últimas notícias{' '}
                <Image
                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-logo-wm1.svg`}
                    alt="logo wm1"
                    loading="lazy"
                    width={119}
                    height={32}
                />
            </h2>
            <div className="news__carousel" ref={emblaRef} data-testid="news-carousel" id="news-carousel">
                <div className="news__container" data-testid="news-container" id="news-container">
                    {safeNews && news.map((newsItem, index) => (
                        <div
                            onClick={() => handleDispatchInteractionEvent('ultimas-noticias-wm1-resultado')}
                            className="news__slide"
                            key={newsItem.Titulo}
                            data-testid={`news-slide-${index}`}
                            id={`news-slide-${index}`}
                        >
                            <a
                                href={newsItem.Url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="news__card__link"
                                data-testid={`news-card-link-${index}`}
                                id={`news-card-link-${index}`}
                            >
                                <div className="news__card" data-testid={`news-card-${index}`} id={`news-card-${index}`}>
                                    <Image
                                        src={newsItem.UrlPrincipalImage}
                                        width={242}
                                        height={181}
                                        alt={newsItem.Titulo || 'Notícia sem título'}
                                        loading="lazy"
                                    />
                                    <p
                                        className="news__card__last-notice"
                                        data-testid={`news-card-last-notice-${index}`}
                                        id={`news-card-last-notice-${index}`}
                                    >
                                        <span>/</span> Ultimas notícias
                                    </p>

                                    <p
                                        className="news__card__title"
                                        data-testid={`news-card-title-${index}`}
                                        id={`news-card-title-${index}`}
                                    >
                                        {newsItem.Titulo}
                                    </p>
                                </div>
                            </a>
                        </div>
                    ))}
                </div>

                {selectedIndex !== 0 && (
                    <button
                        className="news__carousel__prev"
                        onClick={() => emblaApi?.scrollTo(selectedIndex - 1)}
                        data-testid="news-carousel-prev"
                        id="news-carousel-prev"
                    >
                        <Image
                            src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-arrow-carousel-right.svg`}
                            width={10}
                            height={19}
                            alt="icon arrow carousel"
                            loading="lazy"
                        />
                    </button>
                )}
                <button
                    className="news__carousel__next"
                    onClick={() => emblaApi?.scrollTo(selectedIndex + 1)}
                    data-testid="news-carousel-next"
                    id="news-carousel-next"
                >
                    <Image
                        src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-arrow-carousel-right.svg`}
                        width={10}
                        height={19}
                        alt="icon arrow carousel"
                        loading="lazy"
                    />
                </button>

                <div
                    className="news__carousel__dots"
                    data-testid="news-carousel-dots"
                    id="news-carousel-dots"
                >
                    {safeNews && news.map((_, index) => (
                        <button
                            key={index}
                            className={clsx(selectedIndex === index && 'isActive')}
                            onClick={() => emblaApi?.scrollTo(index)}
                            data-testid={`news-carousel-dot-${index}`}
                            id={`news-carousel-dot-${index}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default News;
